/**
 * wayai report create — submit a platform bug report to the triage queue.
 *
 * Usage:
 *   wayai report create --title "Title" --description "Description"
 *   wayai report create -t "Title" -d "Description" --hub <uuid> --error "Error msg"
 *
 * Auto-captures hub/org context from the repo's org binding if present.
 *
 * The report lands in the platform_report queue (status=pending). A Claude
 * Code Desktop routine triages on a 15-min cadence: groups by root cause,
 * dismisses noise, escalates actionable clusters by opening one GitHub issue
 * per group. You'll receive a follow-up email on escalation, on resolution
 * (fix shipped), or on dismissal (polite-no with the reason). If your report
 * is silently grouped as a duplicate of another, you won't get a separate
 * email — you'll only hear from us when the underlying issue is resolved.
 */

import { requireAuth } from '../lib/auth.js';
import { readRepoConfig } from '../lib/repo-config.js';
import { ApiClient, ApiError } from '../lib/api-client.js';
import { summarizeActiveAlertsForReport } from '../lib/hub-alerts.js';
import {
  resolveVersionStatus,
  isVersionBehind,
  formatClientVersion,
  buildStaleToolingWarning,
} from '../lib/version-status.js';

/**
 * The gate honors only the explicit "don't do version checking" switches — NOT `CI` or a non-TTY
 * stderr (unlike the cosmetic update nudge, which suppresses itself in those contexts). The gate is
 * functional — it changes the exit code and prints remediation an agent acts on — so it isn't
 * silenced by CI/piped output. Its signal is still best-effort: it only bites when the local version
 * cache already knows a newer release (warmed by prior commands / session-start bootstrap) and fails
 * open otherwise (see version-status.ts).
 */
function isVersionGateDisabled(): boolean {
  return Boolean(process.env.NO_UPDATE_NOTIFIER || process.env.WAYAI_NO_UPDATE_CHECK);
}

export async function reportCreateCommand(args: string[], cliVersion: string | null = null): Promise<void> {
  // Parse args
  let title: string | undefined;
  let description: string | undefined;
  let source: string | undefined;
  let classification: string | undefined;
  let dedupKey: string | undefined;
  let hubId: string | undefined;
  let conversationId: string | undefined;
  let errorMessage: string | undefined;
  let steps: string | undefined;
  let context: string | undefined;
  let skipVersionCheck = false;

  for (let i = 0; i < args.length; i++) {
    const arg = args[i];
    if (arg === '--skip-version-check') {
      skipVersionCheck = true;
    } else if (arg === '--title' || arg === '-t') {
      title = args[++i];
    } else if (arg === '--description' || arg === '-d') {
      description = args[++i];
    } else if (arg === '--source') {
      source = args[++i];
    } else if (arg === '--classification') {
      classification = args[++i];
    } else if (arg === '--dedup-key') {
      dedupKey = args[++i];
    } else if (arg === '--hub') {
      hubId = args[++i];
    } else if (arg === '--conversation') {
      conversationId = args[++i];
    } else if (arg === '--error' || arg === '-e') {
      errorMessage = args[++i];
    } else if (arg === '--steps' || arg === '-s') {
      steps = args[++i];
    } else if (arg === '--context') {
      context = args[++i];
    }
  }

  if (!title || !description) {
    console.error('Usage: wayai report create --title "Title" --description "Description"');
    console.error('');
    console.error('Required:');
    console.error('  -t, --title <text>         Bug title');
    console.error('  -d, --description <text>   Detailed description');
    console.error('');
    console.error('Optional:');
    console.error('  --source <value>           Origin: cli (default) | review | security_audit');
    console.error('  --classification <value>   Nature: bug (default) | flaky_test | security | enhancement');
    console.error('  --dedup-key <text>         Stable dedup key (e.g. "<file>::<test>")');
    console.error('  --hub <uuid>               Hub ID (optional context for the bug report)');
    console.error('  --conversation <uuid>      Conversation ID');
    console.error('  -e, --error <text>         Error message or stack trace');
    console.error('  -s, --steps <text>         Steps to reproduce');
    console.error('  --context <text>           Additional context');
    console.error('  --skip-version-check       File even when the CLI/skill is behind the latest release');
    process.exit(1);
  }

  // Pre-flight freshness gate: a stale CLI or skill is the most common cause of a false bug report
  // (the issue is often already fixed upstream). Resolve from the local version cache (no network),
  // stamp the CLI + skill version onto the report, and refuse to file on stale tooling unless the
  // caller explicitly opts out. Fails open when freshness is unknown (cold cache) or version
  // checking is disabled.
  const versionStatus = resolveVersionStatus(cliVersion);
  const clientVersion = formatClientVersion(versionStatus);
  if (isVersionBehind(versionStatus) && !isVersionGateDisabled()) {
    const warning = buildStaleToolingWarning(versionStatus);
    if (!skipVersionCheck) {
      console.error(warning);
      console.error('');
      console.error('To file this report anyway, re-run with --skip-version-check.');
      process.exit(1);
    }
    // Escape hatch used — proceed, but make the stale tooling visible.
    console.error('Filing on stale tooling (--skip-version-check):');
    console.error(warning);
    console.error('');
  }

  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });

  // Auto-capture from the repo's org binding (optional — don't fail if missing)
  const repoConfig = readRepoConfig();
  const organizationId = repoConfig?.organization_id;

  // When the report names a hub, auto-attach its active connection/credential
  // alerts. Many "bug" reports (e.g. "TTS not delivered") are actually an invalid
  // provider credential the reporter can't see — folding the alert summary into
  // the report lets triage catch it up front. Best-effort: a failure here (or an
  // older backend without the alerts route) must never block filing the report.
  let contextWithAlerts = context;
  if (hubId) {
    try {
      const { alerts } = await client.getHubAlerts(hubId);
      const alertSummary = summarizeActiveAlertsForReport(alerts);
      if (alertSummary) {
        contextWithAlerts = context ? `${context}\n\n${alertSummary}` : alertSummary;
      }
    } catch {
      // ignore — alerts are supplementary context, not a filing prerequisite
    }
  }

  try {
    const result = await client.submitReport({
      title,
      description,
      source,
      classification,
      dedup_key: dedupKey,
      organization_id: organizationId,
      hub_id: hubId,
      conversation_id: conversationId,
      error_message: errorMessage,
      steps_to_reproduce: steps,
      additional_context: contextWithAlerts,
      client_version: clientVersion,
    });

    if (result.created) {
      console.log(`Report submitted: ${result.report_id}`);
    } else if (result.recurrence?.reopened) {
      // The same failure came back after its report had been resolved — say so, or the
      // reporter reads a bare "merged" as "nothing happened, you're a duplicate".
      console.log(`Report reopened — this was resolved before and has recurred: ${result.report_id}`);
    } else {
      console.log(`Report merged with existing: ${result.report_id}`);
    }
    console.log('A WayAI engineer (or one of our AI agents) will triage your report.');
    console.log('You\'ll get an email on escalation, resolution, or dismissal — if your');
    console.log('report is silently grouped as a duplicate, you won\'t get a separate email.');
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.status === 429) {
        console.error('Rate limit exceeded. Please try again later.');
      } else if (err.status === 503) {
        console.error('Report submission is not currently available.');
      } else {
        console.error(`Failed to submit report: ${err.message}`);
      }
      process.exit(1);
    }
    throw err;
  }
}
